import { isRejectedWithValue, Middleware } from '@reduxjs/toolkit';

import { notifyError } from 'shared/Toast/Toast';

interface Payload {
  data?: {
    message?: string;
  };
  status: number | string;
}

/**
 * Turns RTK Query rejections into the global error toast: a 500 shows the platform's message (or a
 * generic one), a network failure against the platform shows the "server is updating" notice.
 *
 * Rejections from the API slices named in `silentReducerPaths` are left alone. Their data feeds
 * surfaces that are absent for most patients, so a server error there would put a toast in front
 * of people with nothing at stake. The action still rejects, so consumers keep RTK Query's error
 * state and its last successful payload; only the toast is skipped. See `SILENT_ERROR_REDUCER_PATHS`
 * in `store/index.ts` for the list and the reasons.
 */
export const createRtkQueryErrorLogger =
  (silentReducerPaths: readonly string[]): Middleware =>
  () =>
  (next) =>
  (action) => {
    if (isRejectedWithValue(action) && !isSilentErrorAction(action.type, silentReducerPaths)) {
      const payload = action.payload as Payload;

      if (payload && typeof payload === 'object' && 'status' in payload) {
        if (payload.status === 500 && payload.data?.message !== 'Subscription does not exist') {
          notifyError(
            payload.data?.message ||
              'Something is wrong with the server, and we are already working on fixing it.'
          );
          // eslint-disable-next-line no-console
          console.error(payload.data?.message);
        } else if (payload.status === 'FETCH_ERROR') {
          const meta = (action as { meta?: { baseQueryMeta?: { request?: { url?: string } } } })
            .meta;
          if (meta?.baseQueryMeta?.request?.url?.includes('lifemd')) {
            notifyError('Server is updating at the moment, we need a couple of minutes', false, {
              toastId: 'fetch_error'
            });
          }
          // eslint-disable-next-line no-console
          console.error('CORS ERROR');
        }
      }
    }

    return next(action);
  };

const isSilentErrorAction = (type: unknown, silentReducerPaths: readonly string[]): boolean =>
  typeof type === 'string' && silentReducerPaths.some((path) => type.startsWith(`${path}/`));
